import type { ReactNode } from "react";

import logoUrl from "../assets/postmath-logo.svg";
import { CONSOLE_BASE } from "../lib/config";
import { cx } from "../lib/cx";
import { initial } from "../lib/format";
import { PERM, can } from "../lib/rbac";
import { useAction } from "../lib/useAction";
import type { Route } from "../lib/routing";
import type { Session } from "../lib/types";
import { AppLink } from "./AppLink";
import { Button } from "./Button";

/**
 * 앱 셸(헤더 + 본문) — 원본 `app.css` 의 `.shell-*` / `.page` 와 `app.js` 의 `shell()`.
 *
 * 메뉴는 **보유 권한에 따라** 나타납니다(`plt:member:manage` → 사용자, `plt:site:manage` → 카탈로그).
 * 권한 0 로그인을 허용하는 정책이라 최소 Role 사용자는 "서비스" 하나만 봅니다.
 *
 * ⚠️ 반응형 분기점은 Tailwind 기본 `sm:`(min-width 640px)을 씁니다. 사내 CSS 관례는
 * `max-width: 600px` 이지만 ① 원본을 그대로 옮기려면 커스텀 스크린을 `tailwind.config.js` 에
 * 추가해야 하고 그 파일은 사내 토큰 정본이라 건드리지 않으며, ② 600–639px 구간에서 달라지는 것은
 * 헤더 줄바꿈과 제목 크기뿐이라 시각적 영향이 없습니다. 기본값을 택해 설정 파일을 깨끗하게 둡니다.
 */

const NAV_LINK =
  "whitespace-nowrap rounded-[10px] px-[12px] py-[7px] text-[14px] font-semibold text-grey-600 " +
  "hover:bg-grey-050 hover:no-underline";
const NAV_LINK_ACTIVE = "bg-blue-025 text-blue-500";

interface NavItem {
  path: Route;
  label: string;
}

export function Shell({
  route,
  session,
  onNavigate,
  onLogout,
  children,
}: {
  route: Route;
  session: Session;
  onNavigate: (href: string) => void;
  /** 끝날 때까지 버튼을 진행 상태로 둡니다(중복 로그아웃 요청 방지). */
  onLogout: () => Promise<void>;
  children: ReactNode;
}) {
  const logout = useAction();
  const items: NavItem[] = [
    { path: "/gallery", label: "서비스" },
    ...(can(session, PERM.memberManage)
      ? ([{ path: "/members", label: "사용자" }] as NavItem[])
      : []),
    ...(can(session, PERM.siteManage) ? ([{ path: "/sites", label: "카탈로그" }] as NavItem[]) : []),
  ];

  const name = session.member.displayName;

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-grey-100 bg-white/[0.88] backdrop-blur-[10px]">
        <div className="mx-auto flex max-w-[1080px] flex-wrap items-center gap-[10px] px-[16px] py-[12px] sm:flex-nowrap sm:gap-[16px] sm:px-[24px]">
          <AppLink
            href={`${CONSOLE_BASE}/gallery`}
            onNavigate={onNavigate}
            className="flex items-center gap-[10px] text-[15px] font-bold tracking-[-0.02em] text-grey-900 hover:no-underline"
          >
            {/* 옆의 "통합 로그인" 글자가 링크 이름이므로 로고는 장식입니다(alt=""). */}
            <img src={logoUrl} alt="" width={27} height={28} className="h-[28px] w-auto" />
            <span>통합 로그인</span>
          </AppLink>

          {/* 모바일에서는 메뉴를 셋째 줄 전폭으로 내리고 가로 스크롤시킵니다(원본과 동일). */}
          <nav
            aria-label="콘솔 메뉴"
            className="order-3 flex w-full gap-[4px] overflow-x-auto sm:order-none sm:ml-[8px] sm:w-auto sm:overflow-visible"
          >
            {items.map((item) => (
              <AppLink
                key={item.path}
                href={`${CONSOLE_BASE}${item.path}`}
                onNavigate={onNavigate}
                ariaCurrent={route === item.path}
                className={cx(NAV_LINK, route === item.path && NAV_LINK_ACTIVE)}
              >
                {item.label}
              </AppLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-[10px]">
            <span
              aria-hidden="true"
              className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-blue-025 text-[13px] font-bold text-blue-500"
            >
              {initial(name)}
            </span>
            {/* 좁은 화면에서는 이름을 숨깁니다 — 아바타와 로그아웃 버튼이 먼저입니다. */}
            <span className="hidden text-[13px] font-semibold text-grey-700 sm:inline">{name}</span>
            <Button variant="ghost" pending={logout.pending} onClick={() => void logout.run(onLogout)}>
              로그아웃
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1080px] px-[16px] pb-[64px] pt-[24px] sm:px-[24px] sm:pt-[32px]">
        {children}
      </main>
    </>
  );
}
